import * as React from 'react';

import { Route, Router, Switch } from 'react-router-dom';

import { AppVersionWatcher } from 'AppVersionWatcher/AppVersionWatcher';
import AuthenticatedRoutesContainer from 'AuthenticatedRoutesContainer/AuthenticatedRoutesContainer';
import browserHistory from 'BrowserHistory';
import { DialogContainer } from 'common/components/core/DialogProvider';
import FullScreenGlobalLoadingWrapper from 'common/components/core/FullScreenLoader/FullScreenGlobalLoadingWrapper/FullScreenGlobalLoadingWrapper';
import RestrictedRoute from 'common/components/core/RestrictedRoute/RestrictedRoute';
import GlobalLoader from 'common/components/GlobalLoader/GlobalLoader';
import LiveAnnouncerRenderer from 'common/components/LiveAnnouncer/LiveAnnouncerRenderer';
import OfflineStateRenderer from 'common/components/OfflineState/OfflineStateRenderer';
import Permissions from 'common/constants/permissions';
import { isTokenAvailable } from 'common/helpers/jwtHelper';
import With404Route from 'NoAccess/With404Route';
import AuthenticationError from 'Root/AuthenticationError/AuthenticationError';
import Root from 'Root/Root';
import { AppRoutes } from 'routes';
import UnsavedChangesListener from 'UnsavedChangesListener/UnsavedChangesListener';

export interface AppContentProps {
  shouldLogin: boolean;
}

const AuthenticationProviderSelection = React.lazy(
  () => import('AuthenticationProviderSelection/AuthenticationProviderSelection')
);
const TermsOfService = React.lazy(() => import('TermsOfService/TermsOfService'));
const ClusterDashboard = React.lazy(() => import('ClusterDashboard/ClusterDashboard'));
const CreateClusterPage = React.lazy(() => import('Admin/Clusters/CreateClusterDialog/CreateClusterPage'));
const RecreateClusterPage = React.lazy(
  () => import('ClusterDashboard/ClusterSummaryPanel/ClusterRecoveryWidget/RecreateClusterDialog/RecreateClusterPage')
);
const Admin = React.lazy(() => import('Admin/Admin'));
const Task = React.lazy(() => import('Task/Task'));
const Profile = React.lazy(() => import('Profile/Profile'));
const CLUSTER_PAGE_PERMISSIONS = [Permissions.CLUSTER, Permissions.CREATE_CLUSTER];

export default function AppContent({ shouldLogin }: AppContentProps) {
  return (
    <>
      <LiveAnnouncerRenderer />
      <OfflineStateRenderer />
      <FullScreenGlobalLoadingWrapper />
      <AppVersionWatcher />
      <React.Suspense fallback={<GlobalLoader />}>
        <Router history={browserHistory}>
          <With404Route>
            <Switch>
              <Route path={AppRoutes.TOS} component={TermsOfService} />
              <Route path={AppRoutes.AUTHORIZATION_ERROR} component={AuthenticationError} />

              {isTokenAvailable() && (
                <AuthenticatedRoutesContainer shouldLogin={shouldLogin}>
                  <UnsavedChangesListener />
                  <Switch>
                    <Route path={AppRoutes.TASK} component={Task} />
                    <RestrictedRoute
                      path={AppRoutes.CREATE_CLUSTER}
                      component={CreateClusterPage}
                      permissions={CLUSTER_PAGE_PERMISSIONS}
                    />
                    <RestrictedRoute
                      path={AppRoutes.RECREATE_CLUSTER}
                      component={RecreateClusterPage}
                      permissions={CLUSTER_PAGE_PERMISSIONS}
                    />
                    <Route path={AppRoutes.CLUSTER_DASHBOARD} component={ClusterDashboard} />
                    <Route path={AppRoutes.CLUSTER_ADMIN} render={props => <Admin {...props} />} />

                    <Route path={AppRoutes.MY_PROFILE} component={Profile} />

                    <Route path={AppRoutes.ROOT} component={Root} />
                  </Switch>
                </AuthenticatedRoutesContainer>
              )}

              {!isTokenAvailable() && (
                <Switch>
                  <Route path={AppRoutes.ROOT} component={AuthenticationProviderSelection} />
                </Switch>
              )}
            </Switch>
            <DialogContainer />
          </With404Route>
        </Router>
      </React.Suspense>
    </>
  );
}
